import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { authCookie, handleLoginFromCookie, logoutCookie, cognitoLogout, LOGOUT } from '@guryou/redux';
import CustomModal from 'components/CustomModal';
import { Icon } from 'components/shared';
import {
  WIDGET_AUTH_BRANDING_SNAPSHOT_KEY,
  WIDGET_AUTH_INTEGRATION_QUERY_KEY,
  WIDGET_AUTH_INTEGRATION_STORAGE_KEY,
  clearWidgetAuthIntegrationContext,
  clearWidgetAuthUserHintFromStorage,
  readWidgetAuthBrandingFromSession,
  WIDGET_AUTH_RETURN_URL_QUERY_KEY,
  WIDGET_AUTH_USER_HINT_KEY,
  writeWidgetAuthReturnUrlToSession,
} from 'constants/widgetIntegrationBranding';
import { useFeelPanelBranding } from 'components/FeelPanelBrandingContext';
import Login from 'containers/authComponents/Login';
import SignUp from 'containers/authComponents/SignUp';
import ForgotPassword from 'containers/authComponents/ForgotPassword';
import { WidgetAuthBrandingProvider, useWidgetAuthBranding } from './WidgetAuthBrandingProvider';

const cssColor = (val, fallback) => {
  if (val == null || (typeof val === 'string' && !val.trim())) {
    return fallback;
  }
  const v = String(val).trim();
  return v.startsWith('#') ? v : `#${v}`;
};

const AuthWidgetContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { integrationId: routeIntegrationId } = useParams();
  const auth = useSelector(state => state.auth);
  /** Redirect to branded panel only after login/register from this widget modal (not cookie/session already present). */
  const pendingPanelRedirectRef = useRef(false);
  const { accentColor, brandName, logoUrl, authButtonStyles } = useWidgetAuthBranding();
  const { setPanelBranding } = useFeelPanelBranding();
  const hasCookieSession = !!authCookie();
  const isLoggedOut = !auth.data;
  const [persistedUser, setPersistedUser] = useState(null);
  const currentUser = auth.data && hasCookieSession ? auth.data : null;

  const [modalOpen, setModalOpen] = useState(false);
  const [view, setView] = useState('login');
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const branding = {
    accentColor,
    logoUrl,
    brandName,
  };

  const abs = authButtonStyles || {};
  const visibleButtons = abs.visibleButtons || 'both';
  const showLoginButton = visibleButtons === 'both' || visibleButtons === 'login';
  const showRegisterButton = visibleButtons === 'both' || visibleButtons === 'register';
  const loginBg = cssColor(abs.loginBackground, accentColor);
  const loginText = cssColor(abs.loginText, '#ffffff');
  const loginBorder = cssColor(abs.loginBorder, loginBg);
  const loginBw =
    abs.loginBorderWidth != null && String(abs.loginBorderWidth).trim() !== '' ? `${String(abs.loginBorderWidth).replace(/px$/i, '')}px` : '0px';
  const loginBorderNone = Number.parseFloat(loginBw) === 0;
  const regBg = cssColor(abs.registerBackground, '#ffffff');
  const regText = cssColor(abs.registerText, accentColor);
  const regBorder = cssColor(abs.registerBorder, accentColor);
  const regBw =
    abs.registerBorderWidth != null && String(abs.registerBorderWidth).trim() !== ''
      ? `${String(abs.registerBorderWidth).replace(/px$/i, '')}px`
      : '2px';
  const radius = abs.borderRadius != null && String(abs.borderRadius).trim() !== '' ? `${String(abs.borderRadius).replace(/px$/i, '')}px` : '0.5rem';

  const loadPersistedUserHint = useCallback(() => {
    try {
      const raw = localStorage.getItem(WIDGET_AUTH_USER_HINT_KEY);
      if (!raw) {
        setPersistedUser(null);
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed?.username || parsed?.email) {
        setPersistedUser(parsed);
      } else {
        setPersistedUser(null);
      }
    } catch (_) {
      setPersistedUser(null);
    }
  }, []);

  const syncAuthFromCookie = useCallback(() => {
    if (!auth.data && authCookie()) {
      dispatch(handleLoginFromCookie(authCookie()));
    }
  }, [auth.data, dispatch]);

  useEffect(() => {
    loadPersistedUserHint();
  }, [loadPersistedUserHint]);

  useEffect(() => {
    if (isLoggedOut) {
      syncAuthFromCookie();
    }
  }, [isLoggedOut, syncAuthFromCookie]);

  useEffect(() => {
    // During widget auth redirect flow, cookie propagation can lag behind auth.data.
    // Avoid resetting auth in this transient state; otherwise user falls back to landing.
    if (pendingPanelRedirectRef.current) {
      return;
    }
    if (auth.data && !hasCookieSession) {
      dispatch({ type: LOGOUT });
    }
  }, [auth.data, hasCookieSession, dispatch]);

  useEffect(() => {
    const handlePageActivated = () => {
      loadPersistedUserHint();
      syncAuthFromCookie();
    };

    const handleStorage = e => {
      if (!e.key || e.key === WIDGET_AUTH_USER_HINT_KEY) {
        loadPersistedUserHint();
      }
    };

    window.addEventListener('focus', handlePageActivated);
    window.addEventListener('pageshow', handlePageActivated);
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handlePageActivated);

    return () => {
      window.removeEventListener('focus', handlePageActivated);
      window.removeEventListener('pageshow', handlePageActivated);
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handlePageActivated);
    };
  }, [syncAuthFromCookie, loadPersistedUserHint]);

  useEffect(() => {
    if (!isLoggedOut) {
      setModalOpen(false);
    }
  }, [isLoggedOut]);

  useEffect(() => {
    if (!auth.data) return;
    const hint = {
      username: auth.data.username || '',
      email: auth.data.email || '',
      userId: auth.data.userId || null,
      updatedAt: Date.now(),
    };
    try {
      localStorage.setItem(WIDGET_AUTH_USER_HINT_KEY, JSON.stringify(hint));
      setPersistedUser(hint);
    } catch (_) {
      // ignore localStorage write failures
    }
  }, [auth.data]);

  useEffect(() => {
    const id = routeIntegrationId != null && routeIntegrationId !== '' ? String(routeIntegrationId) : null;
    if (!auth.data || !id || !pendingPanelRedirectRef.current) {
      return;
    }
    pendingPanelRedirectRef.current = false;
    try {
      sessionStorage.setItem(WIDGET_AUTH_INTEGRATION_STORAGE_KEY, id);
      const primary =
        accentColor && String(accentColor).trim()
          ? String(accentColor)
              .trim()
              .startsWith('#')
            ? String(accentColor).trim()
            : `#${String(accentColor).trim()}`
          : null;
      sessionStorage.setItem(
        WIDGET_AUTH_BRANDING_SNAPSHOT_KEY,
        JSON.stringify({
          integrationId: id,
          logoUrl: logoUrl || null,
          brandName: brandName || '',
          primaryColor: primary,
        })
      );
      setPanelBranding(readWidgetAuthBrandingFromSession());
    } catch (_) {
      /* ignore */
    }
    history.replace(`/?${WIDGET_AUTH_INTEGRATION_QUERY_KEY}=${encodeURIComponent(id)}`);
  }, [auth.data, routeIntegrationId, history, accentColor, brandName, logoUrl, setPanelBranding]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (userMenuOpen && !event.target.closest('[data-widget-auth-user-menu]')) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  useEffect(() => {
    // Keep auth widget background transparent for iframe embedding.
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    const prevHtmlBg = html.style.backgroundColor;
    const prevBodyBg = body.style.backgroundColor;
    const prevRootBg = root?.style.backgroundColor ?? '';

    html.style.backgroundColor = 'transparent';
    body.style.backgroundColor = 'transparent';
    if (root) {
      root.style.backgroundColor = 'transparent';
    }

    return () => {
      html.style.backgroundColor = prevHtmlBg;
      body.style.backgroundColor = prevBodyBg;
      if (root) {
        root.style.backgroundColor = prevRootBg;
      }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openView = params.get('open');
    const returnTo = params.get(WIDGET_AUTH_RETURN_URL_QUERY_KEY);
    if (returnTo) {
      writeWidgetAuthReturnUrlToSession(returnTo);
    }
    if (openView === 'login' || openView === 'register' || openView === 'passwordForgoten') {
      setView(openView);
      setModalOpen(true);
      if (openView === 'login' || openView === 'register') {
        pendingPanelRedirectRef.current = true;
      }
    }
  }, [location.search]);

  const handleWidgetLogout = () => {
    clearWidgetAuthIntegrationContext();
    dispatch({ type: LOGOUT });
    dispatch(cognitoLogout());
    logoutCookie();
    clearWidgetAuthUserHintFromStorage();
    setPersistedUser(null);
    setUserMenuOpen(false);
  };

  const openModal = nextView => {
    pendingPanelRedirectRef.current = nextView === 'login' || nextView === 'register';

    // If user is already authenticated, jump directly to branded panel.
    if (auth.data && authCookie() && routeIntegrationId && (nextView === 'login' || nextView === 'register')) {
      const params = new URLSearchParams();
      params.set(WIDGET_AUTH_INTEGRATION_QUERY_KEY, String(routeIntegrationId));
      if (document.referrer) {
        try {
          const ref = new URL(document.referrer);
          if (ref.protocol === 'https:' || ref.protocol === 'http:') {
            params.set(WIDGET_AUTH_RETURN_URL_QUERY_KEY, document.referrer);
          }
        } catch {
          // invalid URL — skip
        }
      }
      const targetUrl = `${window.location.origin}/?${params.toString()}`;
      try {
        window.top.location.href = targetUrl;
        return;
      } catch (_) {
        window.location.href = targetUrl;
        return;
      }
    }

    // If embedded in an iframe, open auth route in top window to use full-page modal.
    if (window.self !== window.top && routeIntegrationId) {
      const params = new URLSearchParams();
      params.set('open', nextView);
      if (document.referrer) {
        try {
          const ref = new URL(document.referrer);
          if (ref.protocol === 'https:' || ref.protocol === 'http:') {
            params.set(WIDGET_AUTH_RETURN_URL_QUERY_KEY, document.referrer);
          }
        } catch {
          // invalid URL — skip
        }
      }
      const targetUrl = `${window.location.origin}/website-integration-auth/${routeIntegrationId}?${params.toString()}`;
      try {
        window.top.location.href = targetUrl;
        return;
      } catch (_) {
        window.location.href = targetUrl;
        return;
      }
    }

    setView(nextView);
    setModalOpen(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openView = params.get('open');
    if (!(openView === 'login' || openView === 'register')) {
      return;
    }
    if (!routeIntegrationId || !auth.data || !authCookie()) {
      return;
    }
    history.replace(`/?${WIDGET_AUTH_INTEGRATION_QUERY_KEY}=${encodeURIComponent(String(routeIntegrationId))}`);
  }, [location.search, routeIntegrationId, auth.data, history]);

  const handleCloseModal = () => {
    pendingPanelRedirectRef.current = false;
    setModalOpen(false);
    setView('login');
    if (location.search.includes('open=')) {
      history.replace(location.pathname);
    }
  };

  const modalTitle = view ? t(`GENERAL.${view}`) : t('GENERAL.login');

  if (currentUser) {
    return <div className="w-full h-full min-h-[88px] bg-transparent" />;
  }

  return (
    <div className="w-full h-full min-h-[88px] flex items-center justify-center box-border p-0 bg-transparent">
      <div className={`w-full max-w-[360px] flex justify-center ${modalOpen ? 'invisible pointer-events-none' : ''}`}>
        <div className="flex flex-nowrap gap-2 items-stretch w-full justify-center">
          {showLoginButton && (
            <button
              type="button"
              onClick={() => openModal('login')}
              className="min-w-0 flex-1 py-2 px-2 sm:px-3 font-semibold shadow-sm hover:opacity-90 transition-opacity text-center whitespace-nowrap overflow-hidden text-ellipsis"
              style={{
                backgroundColor: loginBg,
                color: loginText,
                borderColor: loginBorder,
                borderWidth: loginBw,
                borderStyle: loginBorderNone ? 'none' : 'solid',
                borderRadius: radius,
                fontSize: 'clamp(11px, 2.6vw, 14px)',
                lineHeight: 1.2,
              }}
            >
              {t('GENERAL.login')}
            </button>
          )}
          {showRegisterButton && (
            <button
              type="button"
              onClick={() => openModal('register')}
              className="min-w-0 flex-1 py-2 px-2 sm:px-3 font-semibold shadow-sm hover:opacity-90 transition-opacity text-center whitespace-nowrap overflow-hidden text-ellipsis"
              style={{
                backgroundColor: regBg,
                color: regText,
                borderColor: regBorder,
                borderWidth: regBw,
                borderStyle: 'solid',
                borderRadius: radius,
                fontSize: 'clamp(11px, 2.6vw, 14px)',
                lineHeight: 1.2,
              }}
            >
              {t('GENERAL.register')}
            </button>
          )}
        </div>
      </div>

      <CustomModal isOpen={modalOpen} onClose={handleCloseModal} title={modalTitle} disableClose hideCloseButton>
        {view === 'login' ? (
          <Login setView={setView} branding={branding} isWidgetAuthFlow />
        ) : view === 'register' ? (
          <SignUp setView={setView} branding={branding} isWidgetAuthFlow handleCloseModal={handleCloseModal} />
        ) : (
          <ForgotPassword setView={setView} branding={branding} />
        )}
      </CustomModal>
    </div>
  );
};

const WidgetAuth = () => (
  <WidgetAuthBrandingProvider>
    <AuthWidgetContent />
  </WidgetAuthBrandingProvider>
);

export default WidgetAuth;
